// Camada de API do módulo workflow-engine (T051).
//
// REGRA HARD (CLAUDE.md): nenhum componente chama `con.*` direto — tudo passa
// por aqui, e os componentes/views chamam via store dispatch. Toda resposta
// segue o contrato AAWZ: HTTP 200 sempre, status real em `r.status` no body.
//
// US-8: as rotas do form recebem SÓ o token. O frontend nunca envia
// id_escritorio/id_workflow — o backend resolve o office do token (SSOT) e
// valida o match com o office da sessão (gate P1).

import con from '@services/connection'
import { formatQueryParams } from '@utils/Format'
import { URL } from '@modules/workflow-engine/constants/URL'

const FALLBACK_ERROR = { status: 500, message: 'Erro inesperado', value: null }

// O connection.js só serializa arrays via JSON.stringify; um objeto puro vira
// "[object Object]" no FormData e chega ilegível ao backend. Os campos que são
// objeto aninhado (config, sample_payload) precisam ser stringificados aqui — o
// backend faz json.loads best-effort de cada campo do form (padrão dos CRUDs).
const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

// Devolve uma CÓPIA do payload com `key` como JSON string (sem mutar o objeto
// reativo do componente); no-op quando o campo não é um objeto puro.
const withStringifiedField = (payload, key) => {
  if (!payload || !isPlainObject(payload[key])) return payload
  return { ...payload, [key]: JSON.stringify(payload[key]) }
}

class WorkflowEngineApi {
  // ---------------------------------------------------------------------------
  // Registry (US-1) — INTERNO
  // ---------------------------------------------------------------------------

  // Lista triggers do escritório da sessão (filtros opcionais).
  async getTriggers({ idWorkflow = null, tipo = null, ativo = null } = {}) {
    const query = formatQueryParams({
      id_workflow: idWorkflow,
      tipo,
      ativo,
    })
    const r = await con.get(URL.triggers + query)
    return r || FALLBACK_ERROR
  }

  // Cria um trigger. NÃO envia tenancy (sessão supre id_escritorio — P1).
  // `config` é objeto aninhado → stringificado para sobreviver ao FormData.
  async createTrigger({ data } = {}) {
    const r = await con.post(URL.triggers, withStringifiedField(data, 'config'))
    return r || FALLBACK_ERROR
  }

  // Edita um trigger (PUT /trigger/<id>) — persiste config.fields[] (US-5).
  // `config` é objeto aninhado → stringificado para sobreviver ao FormData.
  async saveTrigger({ id, data } = {}) {
    const r = await con.put(URL.trigger(id), withStringifiedField(data, 'config'))
    return r || FALLBACK_ERROR
  }

  // Ativa/desativa um trigger (POST /trigger/<id>/active).
  async setTriggerActive({ id, ativo } = {}) {
    const r = await con.post(URL.triggerActive(id), { ativo })
    return r || FALLBACK_ERROR
  }

  // Remove um trigger (DELETE /trigger/<id>).
  async deleteTrigger({ id } = {}) {
    const r = await con.delet(URL.trigger(id))
    return r || FALLBACK_ERROR
  }

  // Test-fire (US-7) — dispara o workflow com payload de exemplo, sem esperar a
  // condição real. sample_payload é opcional (ignorado para cron).
  async testFire({ id, samplePayload = null } = {}) {
    // `sample_payload` é objeto aninhado → stringificado para sobreviver ao FormData.
    const body = samplePayload ? { sample_payload: samplePayload } : {}
    const r = await con.post(
      URL.triggerTestFire(id),
      withStringifiedField(body, 'sample_payload')
    )
    return r || FALLBACK_ERROR
  }

  // ---------------------------------------------------------------------------
  // Workflows (spec-004 canvas) — INTERNO, escopado por sessão. CRUD + run.
  // ---------------------------------------------------------------------------

  // Lista os workflows do escritório da sessão (filtro opcional por `ativo`).
  async getWorkflows({ ativo = null } = {}) {
    const query = formatQueryParams({ ativo })
    const r = await con.get(URL.workflows + query)
    return r || FALLBACK_ERROR
  }

  // Detalhe completo de um workflow (nodes + connections inclusos).
  async getWorkflow({ id } = {}) {
    const r = await con.get(URL.workflow(id))
    return r || FALLBACK_ERROR
  }

  // Cria um workflow em rascunho (ativo=false). NÃO envia tenancy (sessão supre — P1).
  async createWorkflow({ data } = {}) {
    const r = await con.post(URL.workflows, data)
    return r || FALLBACK_ERROR
  }

  // Edita um workflow (nome/descricao/nodes/connections/ativo). PUT /workflow/<id>.
  async saveWorkflow({ id, data } = {}) {
    const r = await con.put(URL.workflow(id), data)
    return r || FALLBACK_ERROR
  }

  // Remove um workflow (DELETE /workflow/<id>). 400 se houver triggers/execuções.
  async deleteWorkflow({ id } = {}) {
    const r = await con.delet(URL.workflow(id))
    return r || FALLBACK_ERROR
  }

  // Executa o workflow síncronamente (POST /workflow/<id>/run). O body aceita
  // `input_items` (payload de entrada da execução manual); mandamos vazio.
  async runWorkflow({ id, inputItems = [] } = {}) {
    const r = await con.post(URL.workflowRun(id), { input_items: inputItems })
    return r || FALLBACK_ERROR
  }

  // Lista execuções de um workflow (filtros opcionais status/limit).
  async getWorkflowExecutions({ id, status = null, limit = null } = {}) {
    const query = formatQueryParams({ status, limit })
    const r = await con.get(URL.workflowExecutions(id) + query)
    return r || FALLBACK_ERROR
  }

  // Detalhe de UMA execução (run_data por nó). GET /execution/<id>.
  async getExecution({ id } = {}) {
    const r = await con.get(URL.execution(id))
    return r || FALLBACK_ERROR
  }

  // Catálogo de tipos de nó disponíveis para montar o grafo. GET /catalog.
  async getCatalog() {
    const r = await con.get(URL.catalog)
    return r || FALLBACK_ERROR
  }

  // ---------------------------------------------------------------------------
  // Form (US-3/US-5) — autenticado (profissional/gestor). SÓ o token (US-8).
  // ---------------------------------------------------------------------------

  // Carrega a definição renderável do form (GET /form/<token>).
  async fetchFormDefinition({ token } = {}) {
    const r = await con.get(URL.form(token))
    return r || FALLBACK_ERROR
  }

  // Submete o form (POST /form/<token>). O body é um mapa
  // { <field.name>: <value> } — extras são descartados pelo backend.
  async submitForm({ token, values } = {}) {
    const r = await con.post(URL.form(token), values)
    return r || FALLBACK_ERROR
  }
}

export const workflowEngineApi = new WorkflowEngineApi()
export default workflowEngineApi
